import { throwError as observableThrowError, timer as observableTimer, Observable, BehaviorSubject, Subscription } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { NotificationsService } from 'angular2-notifications';
import { Configuration } from '../configuration';
import { Helpers } from '../helpers';
import { LoggedInUserService } from './logged.in.user';
import { ForgottenPasswordDto, ResetPasswordDto } from '../models/userAccounts/userAccount.models';
import { PermissionsHub } from '../signalr/permissions.hub';
import { appInsights } from '../app.module';

const jwtDecode = require('jwt-decode');

@Injectable()
export class AuthService implements OnDestroy {
    private newTokenSub: Subscription;
    private refreshSubscription: Subscription;
    private readonly refreshMilliseconds = 1 * 60000;
    private readonly offsetSeconds = 150;
    private authTime: number;
    private expiresIn: number;

    signinSubject = new BehaviorSubject<boolean>(this.tokenNotExpired());

    constructor(
        private http: HttpClient,
        private configuration: Configuration,
        private helpers: Helpers,
        private router: Router,
        private notificationService: NotificationsService,
        private userService: LoggedInUserService,
        private ngZone: NgZone,
        private readonly permissionsHub: PermissionsHub
    ) { }

    login(email: string, password: string): Observable<boolean> {
        const requestBody = this.helpers.uriEncode({
            grant_type: 'password',
            username: email,
            password: password,
            client_id: this.configuration.auth.client,
            client_secret: this.configuration.auth.secret,
            scope: this.configuration.auth.scope
        });

        this.authTime = new Date().valueOf();

        let headers = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/x-www-form-urlencoded');

        return this.http.post(`connect/token`, requestBody, { headers: headers }).pipe(
            map((response: any) => {
                if (response.access_token) {
                    const decodedToken = jwtDecode(response.access_token);
                    appInsights.setAuthenticatedUserContext(email);
                    this.storeTokens(response);
                    this.scheduleRefresh();

                    let auditHeaders = new HttpHeaders();
                    auditHeaders = auditHeaders.set('Content-Type', 'application/json');
                    auditHeaders = auditHeaders.set(this.configuration.auth.currentUserHeaderName, decodedToken.sub);
                    auditHeaders = auditHeaders.set('Authorization', `Bearer ${response.access_token}`);
                    this.http.get(`auditLogs/userlogin`, { headers: auditHeaders }).subscribe();

                    return true;
                }
                throw Error('Login failed');
            }),
            catchError((error: any) => {
                if (error && !error.errorCode && error.error) {
                    error.errorCode = error.error.error_description;
                }
                return observableThrowError(error);
            })
        );
    }

    scheduleRefresh(): void {
        const delay = this.refreshMilliseconds;
        const source = observableTimer(delay);

        this.ngZone.runOutsideAngular(() => {
            this.refreshSubscription = source.subscribe(() => {
                this.ngZone.run(() => {
                    if (this.newTokenSub) this.newTokenSub.unsubscribe();
                    this.newTokenSub = this.getNewToken().subscribe(
                        () => this.scheduleRefresh(),
                        (error: any) => console.log(error)
                    );
                });
            });
        });
    }

    startupTokenRefresh(): void {
        if (this.signinSubject.getValue()) {
            const delay = this.refreshMilliseconds;
            const source = observableTimer(delay);

            source.subscribe(() => {
                if (this.newTokenSub) this.newTokenSub.unsubscribe();
                this.newTokenSub = this.getNewToken().subscribe(
                    () => this.scheduleRefresh(),
                    (error: any) => console.log(error)
                );
            });
        }
    }

    getNewToken(): Observable<any> {
        const refreshToken = AuthHelpers.getToken(this.configuration.auth.jwtRefreshTokenStorageId);
        const params = {
            client_id: this.configuration.auth.client,
            client_secret: this.configuration.auth.secret,
            grant_type: 'refresh_token',
            refresh_token: refreshToken
        };
        const requestBody = this.helpers.uriEncode(params);

        this.authTime = new Date().valueOf();

        let headers = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/x-www-form-urlencoded');

        return this.http.post(`connect/token`, requestBody, { headers: headers }).pipe(
            map((res: any) => {
                if (res.access_token) {
                    this.storeTokens(res);
                    const decodedToken = jwtDecode(res.access_token);
                    appInsights.setAuthenticatedUserContext(decodedToken.name);
                    this.userService.refreshUserClaims();
                }
            }),
            catchError((error: any) => observableThrowError(error))
        );
    }

    unscheduleRefresh(): void {
        if (this.refreshSubscription) {
            this.refreshSubscription.unsubscribe();
        }
    }

    revokeToken(): void {
        const authToken = AuthHelpers.getToken(this.configuration.auth.jwtAuthTokenStorageId);

        if (authToken) {
            const params = {
                client_id: this.configuration.auth.client,
                client_secret: this.configuration.auth.secret,
                token_type_hint: 'access_token',
                token: authToken
            };
            const requestBody = this.helpers.uriEncode(params);

            this.authTime = new Date().valueOf();

            let headers = new HttpHeaders();
            headers = headers.set('Content-Type', 'application/x-www-form-urlencoded');

            this.http.post(`connect/revocation`, requestBody, { headers: headers }).subscribe();
        }

        AuthHelpers.removeToken(this.configuration.auth.jwtAuthTokenStorageId);
        AuthHelpers.removeExp(this.configuration.auth.jwtExpiryTokenStorageId);
    }

    revokeRefreshToken(): void {
        const refreshToken = AuthHelpers.getToken(this.configuration.auth.jwtRefreshTokenStorageId);

        if (refreshToken) {
            const params = {
                client_id: this.configuration.auth.client,
                client_secret: this.configuration.auth.secret,
                token_type_hint: 'refresh_token',
                token: refreshToken
            };
            const requestBody = this.helpers.uriEncode(params);

            this.authTime = new Date().valueOf();

            let headers = new HttpHeaders();
            headers = headers.set('Content-Type', 'application/x-www-form-urlencoded');

            this.http.post(`connect/revocation`, requestBody, { headers: headers }).subscribe();
        }

        AuthHelpers.removeToken(this.configuration.auth.jwtRefreshTokenStorageId);
    }

    logout(expired?: boolean, redirect?: string, revokeOnly: boolean = false): void {
        localStorage.clear();
        this.signinSubject.next(false);
        this.unscheduleRefresh();
        this.revokeToken();
        this.revokeRefreshToken();
        PermissionsHub.stopHub();
        this.userService.logout();

        if (expired) {
            this.notificationService.error('Login Expired', 'Your login has expired, please log in again');
            setTimeout(() => window.location.reload(), 1000);
        }

        if (redirect && !revokeOnly) {
            this.router.navigate([redirect]);
        } else if (!revokeOnly) {
            this.router.navigate(['/account/logout']);
        }
    }

    isSignedIn(): Observable<boolean> {
        return this.signinSubject.asObservable();
    }

    private tokenNotExpired(): boolean {
        const token = AuthHelpers.getToken(this.configuration.auth.jwtAuthTokenStorageId);
        return token != null && (AuthHelpers.getExp(this.configuration.auth.jwtExpiryTokenStorageId) > new Date().valueOf());
    }

    private storeTokens(body: any) {
        AuthHelpers.setToken(this.configuration.auth.jwtAuthTokenStorageId, body.access_token);
        AuthHelpers.setToken(this.configuration.auth.jwtRefreshTokenStorageId, body.refresh_token);
        this.expiresIn = body.expires_in * 1000;
        AuthHelpers.setExp(this.configuration.auth.jwtExpiryTokenStorageId, this.expiresIn);
    }

    ngOnDestroy() {
        if (this.newTokenSub) this.newTokenSub.unsubscribe();
    }

    forgotPassword(model: ForgottenPasswordDto) {
        return this.http.post(`userAccounts/forgottenPassword`, model);
    }

    resetPassword(model: ResetPasswordDto) {
        return this.http.post(`userAccounts/resetPassword`, model);
    }

    canLogin(email: string) {
        let headers = new HttpHeaders();
        headers = headers.set('Accept', 'application/json');
        const params = new HttpParams().set('emailAddress', email);

        return this.http.get(`userAccounts/canLogin`, { headers: headers, params: params }).pipe(
            map((response: any) => {
                if (!response) {
                    this.signinSubject.next(false);
                    this.unscheduleRefresh();
                    this.revokeToken();
                    this.revokeRefreshToken();
                    this.userService.logout();
                }
                return response;
            })
        );
    }

    getProfileStatus(): Observable<{ hasNoCategoriesOrLocations: boolean }> {
        return this.http.get('userAccounts/profileStatus');
    }
}

class AuthHelpers {
    static getToken(name: string): string {
        return localStorage.getItem(name);
    }

    static setToken(name: string, value: string) {
        localStorage.setItem(name, value);
    }

    static removeToken(name: string): void {
        localStorage.removeItem(name);
    }

    static setExp(name: string, exp: number) {
        localStorage.setItem(name, exp.toString());
    }

    static getExp(name: string): number {
        return parseInt(localStorage.getItem(name));
    }

    static removeExp(name: string): void {
        localStorage.removeItem(name);
    }
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./Client/app/auth/auth.service.ts